import classNames from "classnames";
import React from "react";
import defaultImage from "../images/default-product.jpeg";
import { DragAndDropIcon } from "@/icons/primary/drag-and-drop-icon";
import CaStatusDropdown, { CaStatusDropdownOption } from "../status-dropdown/CaStatusDropdown";
import CaButton from "../button/CaButton";
import { PencilIcon } from "@/icons/primary/pencil-icon";
import { ShareIcon } from "@/icons/primary/share-icon";
import { DuplicateIcon } from "@/icons/primary/duplicate-icon";
import { CalendarIcon } from "@/icons/primary/calendar-icon";

export type CaArticleCardProps = {
  className?: string;
  style?: React.CSSProperties;
  name: string;
  date: string;
  status?: string | number | null;
  image?: string;
  actions?: React.ReactNode;
  draggableControlProps?: any;
  isSortable?: boolean;
  onStatusChange?: (status?: string | number | null) => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onShare?: () => void;
};

const STATUS_OPTIONS: CaStatusDropdownOption[] = [
  {
    label: "Public Content",
    value: "public",
    intent: "success",
  },
  {
    label: "Members Only",
    value: "members_only",
    intent: "warning",
  },
  {
    label: "Private Content",
    value: "private",
    intent: "danger",
  },
];

export const CaArticleCard = ({
  className,
  style,
  name,
  date,
  status,
  image,
  actions,
  draggableControlProps,
  isSortable,
  onStatusChange,
  onEdit,
  onDuplicate,
  onShare,
}: CaArticleCardProps) => {
  const imageSrc = image || defaultImage;

  const handleStatusChange = (option: CaStatusDropdownOption) => {
    onStatusChange && onStatusChange(option.value);
  };

  return (
    <div
      className={classNames("flex items-center p-5 bg-white shadow-sm rounded-base", className)}
      style={style}
    >
      <div className='flex items-center gap-5' style={{ flex: "1 0 0" }}>
        {isSortable && (
          <div {...draggableControlProps} className='cursor-move'>
            <DragAndDropIcon className='!text-mid-neutral-3 text-lg' />
          </div>
        )}
        <div className='flex items-center gap-5'>
          <img
            className={classNames("h-16 w-16")}
            src={imageSrc}
            alt='product'
            style={{
              objectFit: "cover",
            }}
          />
          <div className='flex flex-col gap-4 items-start'>
            <h4 className='text-base font-semibold font-sans-serif m-0'>{name}</h4>
            <div className='flex items-center gap-5'>
              <div className='flex items-center gap-3'>
                <CalendarIcon /> {date}
              </div>
              <CaStatusDropdown
                value={status}
                options={STATUS_OPTIONS}
                onChange={handleStatusChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        {actions ? (
          actions
        ) : (
          <>
            {onEdit && (
              <CaButton size='small' minimal onClick={onEdit}>
                <PencilIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
            {onShare && (
              <CaButton size='small' minimal onClick={onShare}>
                <ShareIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
            {onDuplicate && (
              <CaButton size='small' minimal onClick={onDuplicate}>
                <DuplicateIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
          </>
        )}
      </div>
    </div>
  );
};

CaArticleCard.defaultProps = {
  className: "",
  style: {},
  image: undefined,
  status: null,
  actions: null,
  draggableControlProps: {},
  isSortable: true,
  onStatusChange: () => {},
  onEdit: null,
  onDuplicate: null,
  onShare: null,
};
